import { useCallback, useEffect, useRef } from 'react';

type Props<T extends Element = Element> = {
    onResize?: (target: T) => void;
    targetRef?: React.MutableRefObject<T | null>;
};

const useResizeObserver = <T extends Element = Element>({ onResize, targetRef }: Props<T>) => {
    const createdTargetRef = useRef<T>(null);
    const resizeTargetRef = targetRef || createdTargetRef;
    const resizeObserverRef = useRef<ResizeObserver | null>(null);

    const stopResizeObserver = useCallback(() => {
        if (
            resizeObserverRef.current &&
            resizeTargetRef.current &&
            resizeTargetRef.current instanceof Element
        ) {
            resizeObserverRef.current.unobserve(resizeTargetRef.current);
        }
    }, [resizeTargetRef]);

    const startResizeObserver = useCallback(() => {
        if (
            resizeObserverRef.current &&
            resizeTargetRef.current &&
            resizeTargetRef.current instanceof Element
        ) {
            resizeObserverRef.current.observe(resizeTargetRef.current);
        }
    }, [resizeTargetRef]);

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (onResize) {
                    onResize(entry.target as T);
                }
            }
        });
        resizeObserverRef.current = observer;
        startResizeObserver();
        return () => {
            observer.disconnect();
        };
    }, [onResize, startResizeObserver]);

    return {
        resizeTargetRef,
        startResizeObserver,
        stopResizeObserver,
    };
};

export default useResizeObserver;
